import React, { useState } from 'react'
import { Box, HStack, SimpleGrid, Skeleton, Text, VStack } from '@chakra-ui/react'
import { useTranslation } from 'react-i18next'
import { LARGEST_PRICE, LAST_SELECTED_FILTERS_STORAGE_KEY } from 'constants/index'
import { usePropertiesQuery, useCountPropertiesQuery } from 'generated/graphql'
import { SearchInitialValues } from 'types'
import { PropertyCategory, Property } from 'generated/graphql'

import { useHistory } from 'react-router-dom'
import { PropertyResultCard } from 'components/Property'
import { useAuthContext } from 'context/AuthProvider'
import EmptyListHandler from 'components/EmptyHandler'
import { GoogleMap, Pagination, Sort } from 'components'
import { useAppContext } from 'context/AppProvider'
import { useBrowserStorage, useFindLatLong } from 'hooks'

type AdvancedSearchResultsProps = {
  values: SearchInitialValues
  tab: PropertyCategory
  isDirty?: boolean
}

const AdvancedSearchResults: React.FC<AdvancedSearchResultsProps> = ({ values, tab, isDirty }) => {
  const {
    maxPrice,
    minPrice,
    bedRooms,
    bathRooms,
    rentalTerms,
    propertyAvailabilities,
    furnished,
    propertyTypes,
    selectedArea
  } = values

  const { sortType } = useAppContext()
  const [limit, setLimit] = useState(10)
  const [start, setStart] = useState(0)
  const { i18n } = useTranslation()
  const tanslator = i18n as any
  const lang = tanslator.language as string
  const { isAuthenticated } = useAuthContext()
  const history = useHistory()

  const city = selectedArea?.filter((value) => value.typename === 'City')
  const province = selectedArea?.filter((value) => value.typename === 'Province')
  const suburb = selectedArea?.filter((value) => value.typename === 'Suburb')
  const propertyTypeKey = 'type_of_property.name_' + lang
  const propertyTypeInKey = 'type_of_property.name_' + lang + '_in'
  const availableKey = 'property_availability.name_' + lang
  const availableInKey = 'property_availability.name_' + lang + '_in'
  const rentalTermInKey = 'rental_term.name_' + lang + '_in'
  const [localLastSelectedFilters, setLocalLastSelectedFilters] = useBrowserStorage<any>(
    LAST_SELECTED_FILTERS_STORAGE_KEY,
    'local'
  )
  const state = history.location.state as any
  React.useEffect(() => {
    if (isDirty) {
      console.log('isDirty', isDirty)
      setLocalLastSelectedFilters({ ...values, tab, quickSearch: true })
      history.location.state = { ...state, quickSearch: true }
    }
  }, [values, isDirty])

  const variables = {
    sort: sortType,
    limit: 10,
    start: 0,
    where: {
      _or: [
        { 'suburb.slug': suburb?.map((suburb) => suburb.slug) },
        { 'suburb.city.slug': city?.map((city) => city.slug) },
        { 'suburb.city.province.slug': province?.map((province) => province.slug) },
        { [availableKey]: propertyAvailabilities },
        { [propertyTypeKey]: propertyTypes }
      ],
      bathrooms_gte: bathRooms,
      bedrooms_gte: bedRooms,
      price_gte: parseInt(minPrice) || 0,
      price_lte: parseInt(maxPrice) || LARGEST_PRICE,
      'property_category.slug': tab.slug,
      ...(furnished !== null && { furnished: furnished }),
      ...(propertyTypes?.length > 0 && {
        [propertyTypeInKey]: propertyTypes
      }),
      ...(propertyAvailabilities?.length > 0 && {
        [availableInKey]: propertyAvailabilities
      }),
      ...(rentalTerms?.length > 0 && {
        [rentalTermInKey]: rentalTerms
      })
    }
  }

  const { data: propertiesData, loading: propertyDataLoading } = usePropertiesQuery({
    variables,
    fetchPolicy: 'cache-first'
  })

  const { data: count } = useCountPropertiesQuery({ variables })

  const properties = propertiesData?.properties
  const markers = useFindLatLong(properties?.map((property) => property?.street_address || '')).map(
    (marker) => ({
      lat: marker.lat,
      lng: marker.long
    })
  )
  const lat =
    useFindLatLong(properties?.map((property) => property?.street_address || ''))[0]?.lat ||
    -25.83466820438054
  const long = useFindLatLong(properties?.map((property) => property?.street_address || ''))[0]

  return localLastSelectedFilters?.quickSearch ? (
    <>
      <VStack alignItems="flex-start" mt={8} spacing={0}>
        <Text fontSize="xl" fontWeight="extrabold">
          {count?.countProperties} properties found
        </Text>
        <Text fontSize="xs" pb={3}>
          Quick view of properties
        </Text>
      </VStack>
      <HStack spacing={10} alignItems="flex-start">
        <VStack flex={1} alignItems="flex-start">
          <Sort totalResults={count?.countProperties || 0} limit={limit} start={start} />
          <SimpleGrid
            columns={{ base: 1 }}
            w="100%"
            gap={6}
            alignItems="flex-start"
            justifyContent="center"
          >
            {propertyDataLoading ? (
              Array.from(Array(4).keys()).map((n) => (
                <Skeleton width="100%" key={n} rounded="md" height={200} />
              ))
            ) : properties?.length ? (
              properties?.map((property) => (
                <React.Fragment key={property?.id}>
                  <PropertyResultCard
                    expanded={false}
                    onClick={() =>
                      window.open(
                        isAuthenticated
                          ? `/auth/property/${property?.id}`
                          : `/property/${property?.id}`,
                        '_blank'
                      )
                    }
                    property={property as Property}
                    isLoaded={!propertyDataLoading}
                  />
                </React.Fragment>
              ))
            ) : (
              <EmptyListHandler
                title="No Property in this region"
                subTitle="Please try another region or change your filters"
              />
            )}
          </SimpleGrid>
          <Pagination
            setLimit={setLimit}
            py={12}
            start={start}
            setStart={setStart}
            count={count?.countProperties || 0}
            limit={limit}
          />
        </VStack>
        <VStack flex={1.5} alignItems="flex-start">
          <VStack spacing={0} alignItems="flex-start">
            <Text fontWeight="extrabold">Map</Text>
            <Text fontSize="xs" pb={3}>
              Map view of properties
            </Text>
          </VStack>
          <Box w="100%">
            <GoogleMap
              lat={lat || -25.83466820438054}
              lng={long?.long || 28.184194}
              markers={markers}
            />
          </Box>
        </VStack>
      </HStack>
    </>
  ) : (
    <></>
  )
}

export default AdvancedSearchResults
